/** Six independent, rigid crystal modules. Saved Blender geometry; pose-driven formations. */
import * as T from 'three';
import {CrystalWingAirframe} from './crystalWingAirframe';
import {CrystalWingAirflow} from './crystalWingAirflow';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {mergeGeometries} from 'three/addons/utils/BufferGeometryUtils.js';
import {CRYSTAL_WING_ASSET} from './crystalWingAsset.generated';
import {CrystalWingInertia} from './crystalWingInertia';
import {CrystalWingDynamics,CRYSTAL_SPINE_Y,CRYSTAL_SPINE_WIDTH,CRYSTAL_SPINE_HEIGHT,CRYSTAL_SPINE_PATCH_X,CRYSTAL_SPINE_PATCH_Y,type CrystalWingDockAnchor} from './crystalWingDynamics';

import {CrystalLimbMotion,CRYSTAL_LIMB_COUNT,type CrystalLimbAnchors} from './crystalLimbThrusters';
import type {CrystalWingBodyPose} from './crystalWingBody';
import {DIVE_LANDING} from './diveLanding';
import {FLIGHT_LAUNCH_TIME,flightLaunchPulse,flightLaunchWindup} from './flightLaunch';
import {CrystalWingDebris} from './crystalWingDebris';
import {CrystalWeaponMorph,type CrystalWeaponFrame} from './crystalWeapon';
import type {DeathContactResolver} from './deathPhysics';

export const CRYSTAL_BACK_COUNT=6;
export const CRYSTAL_MODULE_COUNT=CRYSTAL_BACK_COUNT+CRYSTAL_LIMB_COUNT;
export const CRYSTAL_PARTS = ['module_hull','module_crystal','module_core','dock_hull','dock_core'] as const;
type Part = typeof CRYSTAL_PARTS[number];
export type CrystalWingAsset = Record<Part,T.BufferGeometry>;
/** Aerodynamic frame before the body swings forward during a glide landing. */
export interface CrystalWingFlightFrame {rotation:T.Quaternion;offset:T.Vector3;weight:number;}
export interface CrystalWingState {
  jet: 'none'|'hover'|'glide'|'dive';
  /** Tangential speed; combine with climb for actual 3D airspeed. */
  speed: number;
  /** Measured travel in the upright body frame, m/s; absent in older previews. */
  lateralSpeed?: number;
  forwardSpeed?: number;
  /** Actual engine effort, 0 coasting / 1 full thrust. */
  power: number;
  /** Free flight deployment clock; paid boost alone leaves this at zero. */
  launchRemaining: number;
  /** Optional 0..1 gathering pose before the controller commits to a dive. */
  divePreparation?: number;
  /** Shared upright-flight pose envelope. Dive uses the existing eased dive pose. */
  verticalAscent?: number;
  /** Short double-jump repositioning cue, independent of flight power. */
  jumpThrust: number;
  aiming: number;
  pitch: number;
  attacking: boolean;
  guarding: boolean;
  compact: boolean;
  grounded: boolean;
  sprinting: boolean;
  /** Attached wall side: -1 left, +1 right, 0 free. */
  wallSide: number;
  /** Reversible unpowered landing anticipation, independent of a dive slam. */
  approach: number;
  strafe: number;
  /** Body yaw in rad/s, positive toward the player's right, around radial up. */
  turnRate: number;
  climb: number;
  forward: number;
  /** Seconds from dive touchdown; negative during anticipation, -1 when inactive. */
  landingTime: number;
  visible: boolean;
  backOffset: number;
}
export const CRYSTAL_WING_REST: Readonly<CrystalWingState> = {
  jet:'none',speed:0,power:1,launchRemaining:0,divePreparation:0,verticalAscent:0,jumpThrust:0,aiming:0,pitch:0,attacking:false,guarding:false,
  compact:false,grounded:true,sprinting:false,wallSide:0,approach:0,strafe:0,turnRate:0,climb:0,forward:0,landingTime:-1,visible:true,backOffset:.21,
};

/** Flatten the five named authoring parts, retaining their facet palette. */
export function extractCrystalWingAsset(scene:T.Object3D):CrystalWingAsset {
  scene.updateMatrixWorld(true);
  const result={} as CrystalWingAsset;
  for(const name of CRYSTAL_PARTS){
    const root=scene.getObjectByName(name);
    if(!root)throw new Error(`Crystal Wings: missing ${name}`);
    const pieces:T.BufferGeometry[]=[];
    root.traverse(o=>{
      if(!(o instanceof T.Mesh))return;
      const g=o.geometry.index?o.geometry.toNonIndexed():o.geometry.clone();
      g.applyMatrix4(o.matrixWorld);
      const material=(Array.isArray(o.material)?o.material[0]:o.material) as T.MeshStandardMaterial;
      const color=material.color??new T.Color(1,1,1),colors=new Float32Array(g.attributes.position.count*3);
      for(let i=0;i<colors.length;i+=3)color.toArray(colors,i);
      g.setAttribute('color',new T.BufferAttribute(colors,3));
      for(const a of Object.keys(g.attributes))if(!['position','normal','color'].includes(a))g.deleteAttribute(a);
      if(!g.attributes.normal)g.computeVertexNormals();
      pieces.push(g);
    });
    if(!pieces.length)throw new Error(`Crystal Wings: empty ${name}`);
    const merged=mergeGeometries(pieces,false);
    for(const g of pieces)g.dispose();
    if(!merged)throw new Error(`Crystal Wings: cannot merge ${name}`);
    merged.computeBoundingBox();merged.computeBoundingSphere();result[name]=merged;
  }
  return result;
}
let template:Promise<CrystalWingAsset>|null=null;
export function loadCrystalWingAsset():Promise<CrystalWingAsset>{
  return template??=(async()=>{
    const response=await fetch(`${CRYSTAL_WING_ASSET.url}?v=${CRYSTAL_WING_ASSET.hash.slice(0,16)}`);
    if(!response.ok)throw new Error(`Crystal Wings: asset request failed (${response.status})`);
    const bytes=await response.arrayBuffer();
    const hash=Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',bytes)),v=>v.toString(16).padStart(2,'0')).join('');
    if(hash!==CRYSTAL_WING_ASSET.hash)throw new Error('Crystal Wings: stale or corrupt asset');
    const gltf=await new GLTFLoader().parseAsync(bytes,'');
    const result=extractCrystalWingAsset(gltf.scene);
    gltf.scene.traverse(o=>{if(o instanceof T.Mesh){o.geometry.dispose();for(const m of Array.isArray(o.material)?o.material:[o.material])m.dispose();}});
    return result;
  })().catch(error=>{template=null;throw error;});
}

const DOWN=new T.Vector3(0,-1,0);
const BACK=new T.Vector3(0,0,1);
const IDENTITY=new T.Quaternion();
const SIZES=[1.05,.90,.66];
const REST_SCALE=.68;
// Rest sits about 6 cm higher, following the back's sagittal curve: the shoulder
// tips lean toward the neck, and the lower rows settle into the lumbar hollow.
// Each rigid blade keeps its own tangent and lane through the existing fold.
const REST=[[.18,.145,.105,.38,.925,.015],[.13,-.075,.095,.08,-.997,.015],[.06,-.325,.050,.14,-.99,.015]];
const REST_PITCH=[-.16,.10,.22];
const HOVER=[[.64,.28,.15,.86,.51,.06],[.79,-.04,.18,.90,-.43,.08],[.48,-.35,.17,.56,-.83,.08]];
const GLIDE=[[.76,.18,.18,.96,-.28,.09],[.71,-.18,.24,.81,-.58,.09],[.42,-.45,.23,.56,-.83,.09]];
// Each piece describes part of a changing wing envelope. Fast flight sweeps the
// blade tips aft; lift opens a broad, cupped fan with a clear trailing edge.
const SOAR=[[.58,.25,.20,.80,.56,.18],[.72,-.20,.25,.90,-.40,.18],[.45,-.51,.26,.58,-.80,.17]];
const SWEPT=[[.57,.43,.23,.42,-.906,.05],[.69,-.14,.27,.40,-.916,.045],[.42,-.59,.26,.24,-.969,.04]];
const BOOST=[[.30,.53,.24,.10,-.994,.035],[.67,-.12,.29,.18,-.983,.035],[.37,-.58,.29,.13,-.991,.025]];
const FLARE=[[1.00,.22,.34,.82,-.12,.56],[.94,-.25,.39,.80,-.35,.49],[.64,-.60,.40,.55,-.73,.40]];
// Narrow, staggered blades trail toward the feet during the head-first plunge.
const DIVE=[[.33,.48,.15,.10,-.995,.025],[.60,-.11,.18,.10,-.995,.025],[.34,-.56,.20,.08,-.997,.025]];
// Dive landing, in the player's upright frame so it holds while the torso
// flips beneath it. Before impact: an airbrake — the fan spread wide and flat
// across the fall. At impact it drives down into a planted cape beside the
// kneeling body, tips toward the ground, then folds home as the body rises.
// Rows sit at increasing depth so each passes behind the one above it.
const LAND_BRAKE=[[.92,.34,.06,.80,.52,.22],[1.04,.04,.40,.96,.12,.25],[.82,-.28,.60,.82,-.48,.28]];
const LAND_PLANT=[[.54,.14,.06,.22,-.96,.18],[.90,-.14,.40,.58,-.80,.16],[.78,-.58,.60,.40,-.90,.12]];
const TUCK=[[.175,.09,.105,.32,.947,.015],[.125,-.14,.105,.08,-.997,.015],[.06,-.38,.105,.12,-.993,.015]];
// Sprinting keeps distinct rows but sweeps the blades behind the pumping arms.
// The upper pair points aft over the shoulders instead of standing like horns.
const SPRINT=[[.36,.24,.12,.23,-.25,.94],[.47,-.12,.20,.19,-.68,.71],[.28,-.42,.22,.12,-.88,.46]];
// Briefly open the compact fan for the double-jump's upward push.
const JUMP=[[.30,.24,.16,.10,-.995,.015],[.43,-.10,.18,.04,-.998,.015],[.25,-.35,.22,.04,-.998,.015]];
// A slim rear chevron: wide shoulders taper into a paired central point.
// A shallow depth curve follows the back instead of flaring away from it.
const VERTICAL_CAPE=[[.56,.34,.34],[.35,-.16,.42],[.18,-.56,.49]] as const;

/** Fixed-size transform state; no skin deformation, world-position integration or physics drift. */
export class CrystalWingMotion {
  readonly positions=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3());
  readonly rotations=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Quaternion());
  readonly scales=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3());
  readonly pulses=new Float32Array(CRYSTAL_BACK_COUNT);
  readonly exhaust=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3(0,-1,0));
  readonly thrustGains=new Float32Array(CRYSTAL_BACK_COUNT).fill(1);
  // The leading pair responds first; the other pairs feather into the turn.
  readonly steering=new Float64Array(3);
  readonly airframe=new CrystalWingAirframe();
  readonly boostFold=new Float64Array(3);
  readonly launchFold=new Float64Array(3);
  launchPulse=0;
  /** Startup light only: an unpowered dive never contributes to thrust. */
  divePulse=0;
  diveWake=0;
  private diveStartRemaining=0;
  private wasDiving=false;
  private diveEffectSeeded=false;
  boostSpread=0;
  readonly approachFold=new Float64Array(3);
  thrust=.08;
  landing=0;
  private time=0;
  private seeded=false;
  private shot=0;
  private flight=0;
  private power=0;
  private glide=0;
  private dive=0;
  private aim=0;
  private supportAim=0;
  private tuck=0;
  private move=0;
  private sprint=0;
  private wasGrounded=true;
  private sprintJump=false;
  private extension=0;
  private wallSide=0;
  private combat=0;
  private turn=0;
  private readonly hardTurn=new Float64Array(2);
  private climb=0;
  private acceleration=0;
  private previousSpeed=0;
  private attackHold=0;
  private formation='';
  private transitionTime=0;
  private transitionDuration=.72;
  private readonly starts=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3());
  private readonly startScales=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3(.72,.72,.72));
  private readonly scaleTarget=new T.Vector3();
  private readonly startRotations=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Quaternion());
  private readonly axialRoll=new T.Quaternion();
  private readonly target=new T.Vector3();
  private readonly direction=new T.Vector3();
  private readonly rotation=new T.Quaternion();
  private readonly feather=new T.Quaternion();
  private readonly pitchRotation=new T.Quaternion();
  private readonly bankRotation=new T.Quaternion();
  private readonly bodyRotation=new T.Quaternion();
  private readonly landingPosition=new T.Vector3();
  private readonly landingRotation=new T.Quaternion();
  private readonly right=new T.Vector3(1,0,0);
  private readonly spineAxis=new T.Vector3(0,1,0);
  private readonly backAxis=new T.Vector3(0,0,1);
  get verticalDive():number{return this.dive;}
  clearVerticalHistory():void{this.dive=0;this.divePulse=this.diveWake=this.diveStartRemaining=0;this.wasDiving=false;this.diveEffectSeeded=false;}
  shoot():void {this.pulses[(this.shot++%2)*3]=1;this.attackHold=.8;}
  update(delta:number,state:Readonly<CrystalWingState>,body?:CrystalWingBodyPose,uprightToTorso: T.Quaternion=IDENTITY,weaponAssembly=false):void {
    const dt=T.MathUtils.clamp(Number.isFinite(delta)?delta:0,0,.1),ease=1-Math.exp(-7*dt);
    this.time+=dt;
    this.attackHold=state.attacking?.8:Math.max(0,this.attackHold-dt);
    const flight=state.jet==='none'?0:1,glide=state.jet==='glide'||state.jet==='dive'?1:0;
    const landingActive=state.landingTime>=-DIVE_LANDING.lead&&state.landingTime<DIVE_LANDING.lock;
    const landing=landingActive?1-T.MathUtils.smootherstep(state.landingTime,.55,DIVE_LANDING.lock):0;
    // Open the airbrake over the anticipation rather than in a few frames.
    this.landing+=(landing-this.landing)*(1-Math.exp(-(state.landingTime<0?10:18)*dt));
    const combat=!weaponAssembly&&(state.attacking||state.guarding)?1:0;
    // Assembly has its own route. Never send all six blades through the
    // attack fan on the way to the gun; only the reserved top pair guards.
    const aim=weaponAssembly?0:Math.max(T.MathUtils.clamp(state.aiming,0,1),combat,this.attackHold>0?1:0),tuck=state.compact?1:0,move=T.MathUtils.smoothstep(Math.abs(state.speed),.6,7);
    // The reserved shoulder pair aims forward on the ground and in hover
    // alike; its hover exhaust is steered separately (writePlume).
    this.supportAim+=((weaponAssembly?T.MathUtils.clamp(state.aiming,0,1):0)-this.supportAim)*(1-Math.exp(-14*dt));
    const divePreparation=state.jet!=='none'&&!state.grounded&&!state.compact&&aim<.15&&!landingActive&&Number.isFinite(state.divePreparation)
      ?T.MathUtils.clamp(state.divePreparation!,0,1):0;
    const diveTarget=state.jet==='dive'?1:divePreparation*.65;
    const diving=state.jet==='dive'&&!state.grounded&&!state.compact&&aim<.15&&!landingActive;
    if(dt>0){
      // The same .4 s startup beat follows Ctrl's .3 s preparation. This is
      // emission only; power, fuel and the settled six-crystal cape are intact.
      if(diving&&!this.wasDiving&&this.diveEffectSeeded)this.diveStartRemaining=FLIGHT_LAUNCH_TIME;
      this.diveStartRemaining=diving?Math.max(0,this.diveStartRemaining-dt):0;
      this.divePulse=diving?flightLaunchPulse(this.diveStartRemaining):this.divePulse*Math.exp(-12*dt);
      if(this.divePulse<1e-5)this.divePulse=0;
      const wake=this.diveStartRemaining>0?1:T.MathUtils.smoothstep(divePreparation,0,1);
      this.diveWake=wake>=this.diveWake?wake:T.MathUtils.lerp(this.diveWake,wake,1-Math.exp(-7*dt));
      if(this.diveWake<1e-5)this.diveWake=0;
      this.wasDiving=diving;
      this.diveEffectSeeded=true;
    }
    const wall=state.compact||state.jet!=='none'?0:T.MathUtils.clamp(state.wallSide,-1,1);
    if(state.grounded||state.jet!=='none'||state.compact||wall!==0)this.sprintJump=false;
    else if(this.wasGrounded)this.sprintJump=state.sprinting||this.sprint>.5;
    this.wasGrounded=state.grounded;
    const sprint=wall!==0||this.sprintJump?1:state.grounded&&state.sprinting?move:0;
    // Walking/running carry the resting fan. Only an actual deployment changes
    // formation; crossing walking speed must not replay the folding arc.
    const formation=tuck?'compact':landingActive?'landing':aim>.15?'attack':wall!==0?`wall${Math.sign(wall)}`:state.jet!=='none'?state.jet:sprint>.1?'sprint':!state.grounded&&state.jumpThrust>.01?'jump':'rest';
    if(formation!==this.formation){
      const leavingDive=this.formation==='dive',sprintChange=this.formation==='sprint'||formation==='sprint'||this.formation.startsWith('wall')||formation.startsWith('wall');
      this.formation=formation;this.transitionTime=0;this.transitionDuration=sprintChange?.42:formation==='rest'?.76:formation==='dive'?.72:formation==='attack'?(leavingDive?.54:.36):.56;
      for(let i=0;i<CRYSTAL_BACK_COUNT;i++){this.starts[i].copy(this.positions[i]);this.startRotations[i].copy(this.rotations[i]);this.startScales[i].copy(this.scales[i]);}
    }
    this.transitionTime+=dt;
    if(!this.seeded){this.flight=flight;this.glide=glide;this.dive=diveTarget;this.aim=aim;this.tuck=tuck;this.move=move;this.sprint=sprint;this.combat=combat;this.wallSide=wall;}
    this.wallSide+=(wall-this.wallSide)*(1-Math.exp(-12*dt));
    this.sprint+=(sprint-this.sprint)*(1-Math.exp(-12*dt));
    this.dive+=(diveTarget-this.dive)*ease;
    this.flight+=(flight-this.flight)*ease;this.glide+=(glide-this.glide)*ease;this.aim+=(aim-this.aim)*ease;
    this.tuck+=(tuck-this.tuck)*ease;this.move+=(move-this.move)*ease;this.combat+=(combat-this.combat)*ease;
    const finite=(v:number)=>Number.isFinite(v)?v:0;
    const speed=finite(state.speed);
    const airspeed=Math.hypot(speed,finite(state.climb));
    const turning=T.MathUtils.clamp(finite(state.turnRate)/1.5+finite(state.strafe)*.24,-1,1);
    // Smooth the command as well as each response: a camera reversal builds
    // angular velocity instead of slamming against the spring's safety tether.
    this.turn+=(turning-this.turn)*(1-Math.exp(-22*dt));
    // Match the authored hard-turn blend. Its inside leg curls back toward
    // the trailing panel, so that panel follows a separate aft lane.
    const flightTilt=Math.abs(Math.atan2(finite(state.climb),Math.max(.01,Math.abs(speed))));
    const hardTurn=state.jet==='glide'&&!state.grounded&&!state.compact&&aim<.15&&!landingActive&&!(state.launchRemaining>0)
      ?T.MathUtils.smoothstep(Math.abs(finite(state.turnRate)),1.35,2.8)
        *(1-.7*T.MathUtils.smoothstep(flightTilt,.15,.75))*(1-T.MathUtils.smoothstep(flightTilt,.8,1.45)):0;
    for(let side=0;side<2;side++){
      const target=state.turnRate*(side===0?-1:1)>0?hardTurn:0;
      // Keep the lane a little longer while the authored leg unfolds.
      this.hardTurn[side]+=(target-this.hardTurn[side])*(1-Math.exp(-(target>this.hardTurn[side]?12:6)*dt));
    }
    this.airframe.update(dt,speed,finite(state.climb),this.turn*1.5,state.jet==='glide'&&!state.compact&&aim<.15&&!landingActive);
    for(let rank=0;rank<3;rank++)this.steering[rank]+=(this.turn-this.steering[rank])*(1-Math.exp(-(6-rank*.7)*dt));
    this.climb+=(T.MathUtils.clamp(finite(state.climb)/12,-1,1)-this.climb)*(1-Math.exp(-5*dt));
    const accelerating=this.seeded&&dt>0&&delta<=.2?T.MathUtils.clamp((speed-this.previousSpeed)/dt/14,-1,1):0;
    this.acceleration+=(accelerating-this.acceleration)*(1-Math.exp(-5*dt));
    if(dt>0||!this.seeded)this.previousSpeed=speed;
    const jumpPulse=state.jet==='none'&&!state.grounded&&!state.compact?T.MathUtils.clamp(state.jumpThrust,0,1):0;
    const jump=jumpPulse*(1-sprint);
    const poweredFlight=state.jet==='glide'||state.jet==='hover';
    this.power+=((poweredFlight?T.MathUtils.clamp(state.power,0,1):0)-this.power)*(1-Math.exp(-12*dt));
    const boost=state.jet==='glide'&&!state.grounded&&!state.compact&&aim<.15&&!landingActive?this.power:0;
    const preparing=state.jet==='glide'&&!state.grounded&&!state.compact&&aim<.15&&!landingActive
      ?flightLaunchWindup(state.launchRemaining):0;
    this.launchPulse=boost>0?flightLaunchPulse(state.launchRemaining)*T.MathUtils.clamp(state.power,0,1):0;
    // The stronger deployment burst is only 0.4 s. Its feather follows twice
    // as quickly while normal paid-boost springs keep their existing motion.
    for(let rank=0;rank<3;rank++)this.launchFold[rank]+=(Math.max(this.launchPulse,preparing*.6)-this.launchFold[rank])*(1-Math.exp(-(28-rank*4)*dt));
    // Thrust at low speed spreads the nozzles; sustained fast travel feathers
    // them tightly aft. Vertical flight must count just as much as level flight.
    const speedFold=T.MathUtils.smoothstep(airspeed,12,48);
    const foldTarget=Math.max(boost*speedFold,preparing*.5);
    for(let rank=0;rank<3;rank++)this.boostFold[rank]+=(foldTarget-this.boostFold[rank])*(1-Math.exp(-(foldTarget>this.boostFold[rank]?9-rank:7+rank)*dt));
    const spread=state.launchRemaining>0?0:boost*(1-speedFold)*(1-divePreparation);
    this.boostSpread+=(spread-this.boostSpread)*(1-Math.exp(-8*dt));
    const approach=state.jet==='glide'&&!state.grounded&&!state.compact&&aim<.15&&!landingActive
      ?T.MathUtils.smootherstep(T.MathUtils.clamp(state.approach,0,1),.08,.88)*(1-this.power):0;
    for(let rank=0;rank<3;rank++)this.approachFold[rank]+=(approach-this.approachFold[rank])*(1-Math.exp(-(9-rank)*dt));
    const extension=Math.max(this.flight,this.sprint,this.aim,jump,this.landing);
    if(!this.seeded)this.extension=extension;
    this.extension+=(extension-this.extension)*(1-Math.exp(-10*dt));
    this.thrust=.07+.10*this.flight+.12*this.move*(1-this.flight)+(poweredFlight?this.flight*this.power*(.33+.40*this.glide):0)+.12*jumpPulse+.24*this.launchPulse;
    if(landingActive)this.thrust=T.MathUtils.lerp(this.thrust,.40*(1-T.MathUtils.smoothstep(state.landingTime,0,.48)),this.landing);
    for(let i=0;i<CRYSTAL_BACK_COUNT;i++){
      const rank=i%3,side=i<3?-1:1,r=REST[rank],h=HOVER[rank],g=GLIDE[rank],t=TUCK[rank];
      const pick=(k:number)=>T.MathUtils.lerp(T.MathUtils.lerp(r[k],SPRINT[rank][k],this.sprint*(1-this.flight)),T.MathUtils.lerp(T.MathUtils.lerp(h[k],g[k],this.glide),DIVE[rank][k],this.dive),this.flight);
      let x=pick(0),y=pick(1),z=pick(2),dx=pick(3),dy=pick(4),dz=pick(5);
      x=T.MathUtils.lerp(x,JUMP[rank][0],jump);y=T.MathUtils.lerp(y,JUMP[rank][1],jump);z=T.MathUtils.lerp(z,JUMP[rank][2],jump);
      dx=T.MathUtils.lerp(dx,JUMP[rank][3],jump);dy=T.MathUtils.lerp(dy,JUMP[rank][4],jump);dz=T.MathUtils.lerp(dz,JUMP[rank][5],jump);
      const morph=this.glide*this.flight*(1-this.dive)*(1-this.landing);
      const aero=this.airframe,shapeSweep=aero.sweep[rank],open=aero.opening[rank];
      const fold=this.boostFold[rank]*(1-open*.25);
      const shape=(k:number)=>T.MathUtils.lerp(T.MathUtils.lerp(T.MathUtils.lerp(SOAR[rank][k],SWEPT[rank][k],shapeSweep),FLARE[rank][k],open),BOOST[rank][k],fold);
      x=T.MathUtils.lerp(x,shape(0),morph);y=T.MathUtils.lerp(y,shape(1),morph);z=T.MathUtils.lerp(z,shape(2),morph);
      dx=T.MathUtils.lerp(dx,shape(3),morph);dy=T.MathUtils.lerp(dy,shape(4),morph);dz=T.MathUtils.lerp(dz,shape(5),morph);
      // An extra close, staggered feather for deployment, then release into
      // the ordinary boost fan. Keep the modules rigid and in separate lanes.
      const launch=this.launchFold[rank]*morph;
      x*=1-launch*[.20,.24,.24][rank];z-=launch*.07;dx*=1-launch*.25;
      // Coasting feathers each facet into a shallow dihedral near the back;
      // the crystal faces remain readable alongside the spread human arms.
      // Leave the powered silhouette and its existing fold untouched.
      const coasting=morph*(1-this.power);
      z-=coasting*[.09,.055,.025][rank];
      // A restrained cape-like fold: the leading edge holds its glide plane,
      // while the trailing rows draw inward and curl upward a little. The
      // staggered response reverses naturally when the approach is cancelled.
      const settle=this.approachFold[rank]*morph;
      x*=1-settle*[.10,.16,.22][rank];
      y+=settle*[0,.02,.045][rank];z+=settle*[.01,.045,.085][rank];
      dx*=1-settle*[.06,.12,.18][rank];dz+=settle*[.025,.09,.16][rank];
      if(rank===0){const clearance=Math.sin(Math.PI*fold)*morph;y+=clearance*.07;z+=clearance*.13;}
      const focus=(weaponAssembly?(rank===0?this.supportAim:0):this.aim)*(1-this.tuck);
      // Every module takes a forward pitch for aiming, attacks and guard. The
      // upper pair focuses strongly; the lower pairs retain a downward fan.
      // The ADS camera looks along the right shoulder, so the whole fan sits
      // below and outboard of that sight line: the shoulder pair at upper-arm
      // height, the lower pairs stepped beneath it (still behind the arms).
      if(rank===0){
        x=T.MathUtils.lerp(x,.76,focus);y=T.MathUtils.lerp(y,-.08,focus);z=T.MathUtils.lerp(z,-.02,focus);
        dx=T.MathUtils.lerp(dx,.08,focus);dy=T.MathUtils.lerp(dy,-Math.sin(state.pitch),focus);dz=T.MathUtils.lerp(dz,-Math.cos(state.pitch),focus);
      }else{
        x=T.MathUtils.lerp(x,rank===1?.62:.40,focus);
        y=T.MathUtils.lerp(y,rank===1?-.34:-.58,focus);
        z=T.MathUtils.lerp(z,.225,focus);
        dx=T.MathUtils.lerp(dx,rank===1?.16:.12,focus);
        const pitch=state.pitch*T.MathUtils.clamp(state.aiming,0,1),down=rank===1?-.72:-.86,forward=rank===1?.66:.50;
        dy=T.MathUtils.lerp(dy,T.MathUtils.lerp(down,-Math.sin(pitch),.45),focus);
        dz=T.MathUtils.lerp(dz,-Math.max(.30,forward*Math.cos(pitch)),focus);
      }
      // Lower modules brace outward for melee/guard, safely behind the arms.
      x+=this.combat*(rank===0?.11:.14);z+=this.combat*.10;
      x=T.MathUtils.lerp(x,t[0]*.88,this.tuck);y=T.MathUtils.lerp(y,t[1]*.8,this.tuck);z=T.MathUtils.lerp(z,t[2],this.tuck);
      dx=T.MathUtils.lerp(dx,t[3],this.tuck);dy=T.MathUtils.lerp(dy,t[4],this.tuck);dz=T.MathUtils.lerp(dz,t[5],this.tuck);
      const phase=this.time*1.45+rank*1.7+side*.6,alive=(1-this.tuck)*(.001+.005*this.flight);
      x+=Math.cos(phase*.73)*alive*.5;y+=Math.sin(phase)*alive;
      // A crystal fan behaves like separate flight surfaces: the inside wing
      // shortens and loses camber; the outside opens and carries the turn.
      // Hover balances in Y; prone flight banks in Z, above/below the torso.
      // Combat/compact poses retain their authored clearance and forward aim.
      const free=(1-this.aim)*(1-this.aim)*(1-this.tuck),air=this.flight*this.flight*free*(1-this.dive*.85)*(1-this.landing)*(1-fold*.28);
      const authority=.55+.45*T.MathUtils.smoothstep(Math.abs(speed),2,26);
      const bank=this.steering[rank]*authority,inside=side*bank;
      const hover=1-this.glide,weight=[1,.78,.52][rank];
      // Two slow, overlapping rhythms avoid a synchronized flap or random jitter.
      const breathe=Math.sin(this.time*2.15-rank*.62+side*.22)*.72+Math.sin(this.time*1.13+rank*.8-side*.45)*.28;
      const balance=Math.sin(this.time*1.63-rank*.48)*side;
      const lift=(breathe*.018+balance*.008)*hover+breathe*.004*this.glide;
      const brake=Math.max(0,-this.acceleration),sweep=this.acceleration*.038+this.climb*.026;
      x+=air*(-inside*(.09+.04*this.glide)*weight+brake*.065*weight+lift*.6);
      y+=air*(-inside*(hover*.080+this.glide*.038)*weight-sweep*weight+lift);
      z+=air*(-inside*this.glide*.068*weight+Math.abs(bank)*.025+brake*.027+hover*(breathe*.008+finite(state.forward)*.025));
      // Ground turns get only a few millimetres of counterweight; the rest fan
      // keeps the same silhouette, spacing and spring anchoring.
      y-=free*(1-this.flight)*this.move*inside*.006*weight;
      this.pulses[i]*=Math.exp(-16*dt);
      this.target.set(side*x,y,state.backOffset+z+this.pulses[i]*.042);
      this.direction.set(side*dx,dy,dz).normalize();
      this.direction.x+=air*side*(-inside*.11+brake*.07)*weight;
      this.direction.y+=air*(-inside*.12+hover*breathe*.045)*weight;
      this.direction.z+=air*(-inside*.17+this.climb*.09-this.acceleration*.07-hover*finite(state.forward)*.12)*weight;
      this.direction.normalize();
      this.rotation.setFromUnitVectors(DOWN,this.direction);
      // Bend the whole facet along the sagittal tangent. Changing only its
      // tip vector would also roll the upward-pointing pair around its axis.
      const resting=1-Math.max(this.flight,this.sprint,this.aim,this.tuck,jump,this.landing);
      this.pitchRotation.setFromAxisAngle(this.right,REST_PITCH[rank]*resting);
      this.rotation.premultiply(this.pitchRotation);
      // Axial feather changes the broad facet's pitch as well as its tip.
      const feather=air*(bank*(.32-rank*.055)+side*(hover*breathe*.06+this.climb*.06+brake*.10+open*.18));
      this.feather.setFromAxisAngle(DOWN,feather);this.rotation.multiply(this.feather);
      this.pitchRotation.setFromAxisAngle(this.right,air*(this.climb*.10-this.acceleration*.09));
      this.rotation.premultiply(this.pitchRotation);
      // Roll the complete fan around the spine: the outside rises as the inside
      // retracts. Keep the low side off the back through sharp, fast reversals.
      this.bankRotation.setFromAxisAngle(this.spineAxis,air*this.glide*aero.bank);
      this.target.z-=state.backOffset+.12;this.target.applyQuaternion(this.bankRotation);this.target.z+=state.backOffset+.12;
      this.target.x+=side*air*this.glide*Math.max(0,-inside)*.075;
      if(air*this.glide>.01)this.target.z=Math.max(state.backOffset+.06,this.target.z);
      this.rotation.premultiply(this.bankRotation);
      const trail=free*(1-this.flight)*this.move*bank*(.055+rank*.012);
      this.direction.copy(this.spineAxis).applyQuaternion(uprightToTorso);
      this.bankRotation.setFromAxisAngle(this.direction,trail);
      this.target.applyQuaternion(this.bankRotation);this.rotation.premultiply(this.bankRotation);
      // The sprint fan rolls away from the wall; the inside row draws inward.
      // Rotate about its own spine anchor, leaving the fitted metal spine alone.
      const wallWeight=Math.abs(this.wallSide)*(1-this.aim)*(1-this.tuck);
      this.target.x*=1-Math.max(0,side*this.wallSide)*.20;
      this.bankRotation.setFromAxisAngle(this.backAxis,this.wallSide*.42*(1-this.aim)*(1-this.tuck));
      this.target.z-=state.backOffset;
      this.target.applyQuaternion(this.bankRotation);this.rotation.premultiply(this.bankRotation);
      this.target.z+=state.backOffset+wallWeight*.035;
      this.target.x-=this.wallSide*.10*(1-this.aim)*(1-this.tuck);
      // Anatomy adds a bounded accent to the formation before spring following.
      // Tight folds and weapon focus keep a softer influence for their clearance.
      const bodyGain=(.24+.51*this.sprint+1.56*this.flight)*(1-this.aim*.75)*(1-this.tuck*.90)*(1-this.dive*.65)*(1-this.landing*.8)*(1-fold*.6)*(1-T.MathUtils.clamp(state.approach,0,1)*.85)*(1-wallWeight*.65);
      if(body?.bound&&body.enabled){
        this.target.addScaledVector(body.positions[i],bodyGain);
        this.bodyRotation.slerpQuaternions(IDENTITY,body.rotations[i],bodyGain);
        this.rotation.premultiply(this.bodyRotation);
      }
      if(rank===2){
        const clearance=this.hardTurn[i<3?0:1]*morph*free;
        this.target.x+=side*.32*clearance;this.target.y-=.36*clearance;this.target.z+=.16*clearance;
      }
      if(this.landing>.0001){
        const plant=T.MathUtils.smootherstep(state.landingTime,-.03,.14),b=LAND_BRAKE[rank],p=LAND_PLANT[rank];
        const l=(k:number)=>T.MathUtils.lerp(b[k],p[k],plant);
        this.landingPosition.set(side*l(0),l(1),state.backOffset+l(2)).applyQuaternion(uprightToTorso);
        this.direction.set(side*l(3),l(4),l(5)).normalize();
        this.landingRotation.setFromUnitVectors(DOWN,this.direction).premultiply(uprightToTorso);
        this.target.lerp(this.landingPosition,this.landing);
        this.rotation.slerp(this.landingRotation,this.landing);
      }
      if(weaponAssembly&&rank===0&&this.supportAim>0){
        // Rifle stance yaws the chest. The sentry pair must point along the
        // player's aim, rather than inheriting that chest yaw as firing yaw.
        this.direction.set(side*.08,-Math.sin(state.pitch),-Math.cos(state.pitch)).normalize().applyQuaternion(uprightToTorso);
        this.landingRotation.setFromUnitVectors(DOWN,this.direction);
        this.rotation.slerp(this.landingRotation,this.supportAim);
      }
      this.exhaust[i].set(air*(-bank*.10+side*hover*breathe*.018),-1,air*(this.climb*.065-this.acceleration*.05+inside*.045)).normalize();
      this.thrustGains[i]=1+air*(-inside*.085+hover*breathe*.04+Math.max(0,this.acceleration)*.06);
      if(!this.seeded||landingActive||this.landing>.01){this.positions[i].copy(this.target);this.rotations[i].copy(this.rotation);}
      else{
        // Clear, feather, then seat: each pair takes a slightly later curved
        // path and a small axial roll before locking into the shallow fan.
        const u=T.MathUtils.clamp((this.transitionTime-rank*.055)/this.transitionDuration,0,1);
        const travel=T.MathUtils.smootherstep(u,0,1),turn=T.MathUtils.smootherstep(u,.10,.90),arc=Math.sin(Math.PI*u);
        this.positions[i].lerpVectors(this.starts[i],this.target,travel);
        this.positions[i].x+=side*arc*(.035+rank*.08);
        this.positions[i].z+=arc*[.12,.09,.15][rank];
        // The long upper blade swings clear behind the next socket before
        // drawing inward into the dive's narrow, trailing arrangement.
        if(this.formation==='dive')this.positions[i].z+=arc*[.42,.12,0][rank];
        this.positions[i].y+=arc*[.015,-.012,-.095][rank];
        // The shoulder pair crosses the right shoulder's sight line on its way
        // out; pass beneath it rather than sweeping through the reticle.
        if(this.formation==='attack'&&rank===0)this.positions[i].y-=arc*.07;
        // Seat along separate lanes: the middle pair clears the forearm pods,
        // and the lowest blades pass below its tips before closing the gap.
        if(this.formation==='rest'){
          if(rank===1)this.positions[i].z+=arc*.03;
          if(rank===2)this.positions[i].y-=arc*.085;
        }
        this.rotations[i].slerpQuaternions(this.startRotations[i],this.rotation,turn);
        this.axialRoll.setFromAxisAngle(DOWN,side*arc*[.20,.32,.24][rank]);this.rotations[i].multiply(this.axialRoll);
      }
      // The middle row turns before the lowest row during the aiming tuck.
      // Let the lower tips pass just behind it, then seat before ignition.
      if(rank===2)this.positions[i].z+=.045*T.MathUtils.smoothstep(preparing,0,.10)*(1-T.MathUtils.smoothstep(preparing,.45,.95));
      const scalePhase=landingActive||this.landing>.01?Math.max(this.landing,flight):this.extension;
      const scale=SIZES[rank]*T.MathUtils.lerp(REST_SCALE,1,scalePhase)*(1-this.tuck*.23);
      // Retract before entering the resting sockets; extend only after clearing
      // them. The staggered path and size change describe the same fold.
      const scaleU=T.MathUtils.clamp((this.transitionTime-rank*.055)/this.transitionDuration,0,1);
      const scaleTravel=scale<this.startScales[i].x?T.MathUtils.smootherstep(scaleU,0,.60):T.MathUtils.smootherstep(scaleU,.25,1);
      if(!this.seeded||landingActive||this.landing>.01)this.scales[i].setScalar(scale);
      else this.scales[i].lerpVectors(this.startScales[i],this.scaleTarget.setScalar(scale),scaleTravel);
    }
    if(!this.seeded){this.transitionTime=2;for(let i=0;i<CRYSTAL_BACK_COUNT;i++){this.starts[i].copy(this.positions[i]);this.startRotations[i].copy(this.rotations[i]);this.startScales[i].copy(this.scales[i]);}}
    else if(landingActive||this.landing>.01){
      // Landing places the modules directly. Keep the next formation's tween
      // anchored to the pose on screen, not to where the landing began.
      this.transitionTime=0;
      for(let i=0;i<CRYSTAL_BACK_COUNT;i++){this.starts[i].copy(this.positions[i]);this.startRotations[i].copy(this.rotations[i]);this.startScales[i].copy(this.scales[i]);}
    }
    this.seeded=true;
  }
}

/** One curved skin patch per metal vertebra. Only four anchor vectors change;
 * source vertices stay on the GPU. The same fit drives light and shadow passes. */
function fitSpineMaterial<M extends T.Material>(material:M):M {
  const previous=material.onBeforeCompile;
  const previousKey=material.customProgramCacheKey();
  material.onBeforeCompile=(shader,renderer)=>{
    previous.call(material,shader,renderer);
    shader.vertexShader=shader.vertexShader.replace('#include <common>',`#include <common>
      attribute vec3 spineLeft,spineRight,spineUp,spineDown;
      vec2 spineUV(vec3 p){return p.xy/vec2(${CRYSTAL_SPINE_PATCH_X},${CRYSTAL_SPINE_PATCH_Y});}
      vec3 spineSurface(vec3 p){
        vec2 v=spineUV(p);
        return .5*(v.x*(spineRight-spineLeft)+v.x*v.x*(spineRight+spineLeft)
          +v.y*(spineUp-spineDown)+v.y*v.y*(spineUp+spineDown))+vec3(0.,0.,p.z);
      }
      vec3 spineNormal(vec3 p,vec3 n){
        vec2 v=spineUV(p);
        vec3 dx=(.5*(spineRight-spineLeft)+v.x*(spineRight+spineLeft))/${CRYSTAL_SPINE_PATCH_X};
        vec3 dy=(.5*(spineUp-spineDown)+v.y*(spineUp+spineDown))/${CRYSTAL_SPINE_PATCH_Y};
        vec3 dz=vec3(0.,0.,1.);
        return mat3(cross(dy,dz),cross(dz,dx),cross(dx,dy))*n;
      }`);
    shader.vertexShader=shader.vertexShader.replace('#include <beginnormal_vertex>','#include <beginnormal_vertex>\nobjectNormal=spineNormal(position,objectNormal);');
    shader.vertexShader=shader.vertexShader.replace('#include <begin_vertex>','vec3 transformed=spineSurface(position);');
  };
  material.customProgramCacheKey=()=> previousKey+'|crystal-spine-surface-v2';
  return material;
}

/** Recolour hue while retaining the authored facet values and pale cuts.
 * Per-avatar uniforms leave the shared GLB geometry and other players alone. */
function tintCrystal(material:T.MeshStandardMaterial,aura:T.Color):void{
  material.onBeforeCompile=shader=>{
    shader.uniforms.crystalAura={value:aura};
    shader.fragmentShader='uniform vec3 crystalAura;\n'+shader.fragmentShader;
    shader.fragmentShader=shader.fragmentShader.replace('#include <color_fragment>',`
      #if defined(USE_COLOR) || defined(USE_COLOR_ALPHA)
      float facet=max(max(vColor.r,vColor.g),vColor.b);
      float pale=min(vColor.r,vColor.g)/max(.001,facet);
      diffuseColor.rgb*=mix(crystalAura,vec3(1.),pale*.30)*(.12+.60*facet);
      #endif`);
    shader.fragmentShader=shader.fragmentShader.replace('#include <emissivemap_fragment>',`#include <emissivemap_fragment>
      #if defined(USE_COLOR) || defined(USE_COLOR_ALPHA)
      totalEmissiveRadiance*=max(max(vColor.r,vColor.g),vColor.b);
      #endif`);
  };
  material.customProgramCacheKey=()=> 'crystal-aura-facets-v1';
}

function glowMaterial(aura:T.Color):T.ShaderMaterial {
  return new T.ShaderMaterial({transparent:true,depthWrite:false,blending:T.AdditiveBlending,toneMapped:false,
    uniforms:{strength:{value:.20},aura:{value:aura}},
    // A small analytic halo, not a light or a bloom pass. Keep the entire solid
    // gemstone readable; the glow falls smoothly to zero before the quad edge.
    vertexShader:`varying vec2 vUv;void main(){vUv=uv;vec4 p=modelViewMatrix*instanceMatrix*vec4(0.,-.10,.025,1.);float s=length((modelViewMatrix*instanceMatrix)[0].xyz);p.xy+=position.xy*s;gl_Position=projectionMatrix*p;}`,
    fragmentShader:`uniform float strength;uniform vec3 aura;varying vec2 vUv;void main(){float r=length((vUv-.5)*2.);float a=exp(-r*r*5.)*(1.-smoothstep(.35,1.,r));gl_FragColor=vec4(aura,a*strength);}`,
  });
}
/** Small travel-driven deflections of the vertical formation. Pair responses
 * settle into a rear V, while lateral travel bends each side independently. */
export class CrystalCapeMotion {
  readonly lean=Array.from({length:3},()=>new T.Vector2());
  readonly axial=new Float64Array(3);
  /** Signed lateral response: +1 tucks/sweeps the right side, opens the left. */
  readonly bend=new Float64Array(3);
  readonly rotations=Array.from({length:3},()=>new T.Quaternion());
  readonly target=new T.Vector2();
  private readonly angles=new T.Euler();
  private previousSpeed=0;
  private seeded=false;
  reset():void{
    this.seeded=false;this.previousSpeed=0;this.target.set(0,0);this.axial.fill(0);this.bend.fill(0);
    for(let rank=0;rank<3;rank++){this.lean[rank].set(0,0);this.rotations[rank].identity();}
  }
  update(dt:number,state:Readonly<CrystalWingState>,amount:number):void{
    if(!Number.isFinite(dt)||dt<=0)return;
    if(dt>.2)this.reset();
    const step=Math.min(dt,.1),finite=(v:number)=>Number.isFinite(v)?v:0;
    const horizontal=Math.abs(finite(state.speed)),climb=finite(state.climb),speed=Math.hypot(horizontal,climb);
    const active=T.MathUtils.smootherstep(amount,.65,1),verticalSign=Math.tanh(climb/5);
    // Ctrl dive steering mostly translates sideways without yawing the body.
    // Read signed carried velocity so the V follows that drift after release,
    // instead of relying on a tiny key-held/yaw accent that vanishes instantly.
    const lateral=Number.isFinite(state.lateralSpeed)?state.lateralSpeed!:finite(state.strafe)*horizontal;
    const forwardSpeed=Number.isFinite(state.forwardSpeed)?state.forwardSpeed!:horizontal;
    const drift=T.MathUtils.clamp(lateral/14,-1,1),turn=T.MathUtils.clamp(finite(state.turnRate)/3,-1,1);
    const intent=T.MathUtils.clamp(finite(state.strafe),-1,1);
    const steering=turn*.08+drift*.06+intent*.02;
    this.target.set(-.10*forwardSpeed/Math.max(8,speed)*verticalSign,-steering*verticalSign*T.MathUtils.smoothstep(speed,4,25));
    const bendTarget=T.MathUtils.clamp(drift*.85+turn*.25+intent*.12,-1,1)*active;
    if(this.target.length()>.11)this.target.setLength(.11);
    this.target.multiplyScalar(active);
    const acceleration=this.seeded&&dt<=.2?T.MathUtils.clamp((speed-this.previousSpeed)/step/35,-1,1)*active:0;
    this.previousSpeed=speed;this.seeded=true;
    for(let rank=0;rank<3;rank++){
      const ease=1-Math.exp(-(18-rank*2)*step);
      this.lean[rank].lerp(this.target,ease);
      this.bend[rank]+=(bendTarget-this.bend[rank])*(1-Math.exp(-(12-rank*2)*step));
      this.axial[rank]+=((rank-1)*.035*acceleration-this.axial[rank])*ease;
      this.rotations[rank].setFromEuler(this.angles.set(this.lean[rank].x,0,this.lean[rank].y));
    }
  }
}

function plumeGeometry():T.BufferGeometry {
  const p:number[]=[],uv:number[]=[];
  for(let side=0;side<3;side++){
    const a=side*Math.PI/3,c=Math.cos(a),s=Math.sin(a);
    for(const [x,y]of [[-1,0],[1,0],[-1,1],[1,0],[1,1],[-1,1]]){
      p.push(x*c,-y,x*s);uv.push((x+1)*.5,y);
    }
  }
  const g=new T.BufferGeometry();g.setAttribute('position',new T.Float32BufferAttribute(p,3));g.setAttribute('uv',new T.Float32BufferAttribute(uv,2));return g;
}
function plumeMaterial(aura:T.Color):T.ShaderMaterial {
  return new T.ShaderMaterial({transparent:true,depthWrite:false,blending:T.AdditiveBlending,side:T.DoubleSide,toneMapped:false,
    uniforms:{strength:{value:.07},time:{value:0},aura:{value:aura},trailLength:{value:.2}},
    vertexShader:`varying vec2 vUv;uniform float strength,time,trailLength;void main(){vUv=uv;vec3 p=position;
      float taper=pow(1.-uv.y,.65),flicker=1.+.07*sin(uv.y*18.-time*19.);
      p.xz*=.092*taper*flicker;p.y*=trailLength;
      gl_Position=projectionMatrix*modelViewMatrix*instanceMatrix*vec4(p,1.);}`,
    fragmentShader:`varying vec2 vUv;uniform float strength,time;uniform vec3 aura;void main(){
      float x=abs(vUv.x*2.-1.),core=exp(-x*x*34.),halo=exp(-x*x*5.);
      float taper=pow(1.-vUv.y,1.35),ripple=.87+.13*sin(vUv.y*27.-time*23.);
      float a=(.38*halo+.32*core)*.55*taper*ripple*smoothstep(.1,.42,strength);
      vec3 light=mix(aura,vec3(1.),core*.50)*(.90+.45*core);
      gl_FragColor=vec4(light,a);
    }`,
  });
}

export class CrystalWings {
  readonly group=new T.Group();
  readonly weaponMorph=new CrystalWeaponMorph();
  weaponFrame:CrystalWeaponFrame|null=null;
  private weaponHover=false;
  readonly motion=new CrystalWingMotion();
  readonly dynamics=new CrystalWingDynamics();
  /** Hinged, inertia-driven secondary motion for the back modules. False
   * restores the original tethered follower (A/B review). */
  naturalMotion=true;
  readonly inertia=new CrystalWingInertia();
  private readonly localTargets=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3());
  private readonly localTargetRotations=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Quaternion());
  private readonly anchorPosition=new T.Vector3();
  readonly limbDynamics=new CrystalWingDynamics(CRYSTAL_LIMB_COUNT,false);
  readonly limbMotion=new CrystalLimbMotion();
  readonly capeMotion=new CrystalCapeMotion();
  readonly auraColor=new T.Color(0xffffff);
  private readonly auraDisplay=new T.Color(0xffffff);
  private readonly energyMaterials:T.MeshStandardMaterial[]=[];
  readonly ready:Promise<void>;
  loaded=false;
  verticalCape=0;
  private capeRate=0;
  private capeWake=0;
  capeSpread=1;
  private capeBackOffset=0;
  private capeSeeded=false;
  private disposed=false;
  private debris:CrystalWingDebris|null=null;
  private deathContact:DeathContactResolver|null=null;
  private deathMatrices:T.Matrix4[][]=[];
  private readonly deathToGroup=new T.Matrix4();
  private readonly instances:T.InstancedMesh[]=[];
  private readonly materials:T.Material[]=[];
  private readonly matrix=new T.Matrix4();
  private readonly local=new T.Vector3();
  private readonly localRotation=new T.Quaternion();
  private readonly frameRotation=new T.Quaternion();
  private readonly inverseRotation=new T.Quaternion();
  private readonly uprightToTorso=new T.Quaternion();
  private readonly flightCompensation=new T.Quaternion();
  private readonly flightOffset=new T.Vector3();
  private readonly flightTarget=new T.Vector3();
  private readonly flightRotation=new T.Quaternion();
  private readonly groupToFrame=new T.Matrix4();
  private readonly frameToGroup=new T.Matrix4();
  private readonly targets=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3());
  private readonly targetRotations=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Quaternion());
  private readonly backScales=Array.from({length:CRYSTAL_BACK_COUNT},()=>new T.Vector3());
  private readonly runTrailing=new T.Vector3();
  private readonly runTrailTarget=new T.Vector3();
  private readonly limbTargets=Array.from({length:CRYSTAL_LIMB_COUNT},()=>new T.Vector3());
  private readonly limbRotations=Array.from({length:CRYSTAL_LIMB_COUNT},()=>new T.Quaternion());
  private readonly worldToFrame=new T.Matrix4();
  private readonly worldToGroup=new T.Matrix4();
  private readonly worldFrameInverse=new T.Quaternion();
  private readonly worldTorso=new T.Quaternion();
  private readonly limbOut=new T.Vector3();
  private readonly limbDelta=new T.Vector3();
  private readonly limbSurface=new T.Vector3();
  private readonly moduleCorners=Array.from({length:8},()=>new T.Vector3());
  // Corners, edge midpoints, face centres and centre: a rigid blade can lie
  // across the sight line with every corner outside the keep-out cone.
  private readonly moduleHull=Array.from({length:27},()=>new T.Vector3());
  /** The local player's camera sight line, in the avatar frame (this group's
   * parent). While aiming, back modules keep out of a cone around the reticle. */
  readonly sight={active:false,origin:new T.Vector3(),dir:new T.Vector3(0,0,-1),up:new T.Vector3(0,1,0),tan:0};
  private readonly sightOrigin=new T.Vector3();
  private readonly sightDir=new T.Vector3();
  private readonly sightUp=new T.Vector3();
  private readonly sightPoint=new T.Vector3();
  private readonly sightRadial=new T.Vector3();
  private readonly sightPush=new T.Vector3();
  private readonly sightInverse=new T.Matrix4();
  private readonly plumePosition=new T.Vector3();
  private readonly plumeRotation=new T.Quaternion();
  private readonly plumeScale=new T.Vector3();
  private readonly diveExhaust=new T.Vector3();
  private readonly capePoint=new T.Vector3();
  private readonly capeAxis=new T.Vector3(0,1,0);
  private readonly capeModuleAxis=new T.Vector3();
  private readonly capeNormal=new T.Vector3();
  private readonly capeRight=new T.Vector3();
  private readonly capeExhaust=new T.Vector3();
  private readonly capeWorldExhaust=new T.Vector3();
  private readonly capeRotation=new T.Quaternion();
  private readonly capeFeather=new T.Quaternion();
  private readonly capeBasis=new T.Matrix4();
  private frame:T.Object3D|null|undefined;
  private readonly color=new T.Color();
  private readonly ownedGeometry:T.BufferGeometry[]=[];
  private dock:T.Group|null=null;
  private readonly spineInstances:T.InstancedMesh[]=[];
  private readonly spineScale=new T.Vector3();
  private readonly spinePatches:T.InstancedBufferAttribute[][]=[];
  private glow:T.ShaderMaterial|null=null;
  private plume:T.ShaderMaterial|null=null;
  private core:T.MeshStandardMaterial|null=null;
  private airflow:CrystalWingAirflow|null=null;
  private readonly airflowSources=Array.from({length:4},()=>new T.Vector3());
  private time=0;
  constructor(parent:T.Object3D,asset?:Promise<CrystalWingAsset>){
    this.group.name='crystal-wings';this.group.visible=false;parent.add(this.group);
    this.ready=(asset??loadCrystalWingAsset()).then(data=>{if(!this.disposed)this.install(data);});
  }
  setAuraColor(hex:number):void{
    this.auraColor.setHex(Number.isFinite(hex)?hex:0xffffff);
    this.auraDisplay.copy(this.auraColor).convertLinearToSRGB();
    for(const material of this.energyMaterials)material.emissive.copy(this.auraColor).lerp(new T.Color(1,1,1),material===this.core?.24:.04);
  }
  private install(asset:CrystalWingAsset):void {
    const bounds=new T.Box3();
    for(const geometry of [asset.module_hull,asset.module_crystal,asset.module_core]){
      if(!geometry.boundingBox)geometry.computeBoundingBox();bounds.union(geometry.boundingBox!);
    }
    for(let i=0;i<8;i++)this.moduleCorners[i].set(i&1?bounds.max.x:bounds.min.x,i&2?bounds.max.y:bounds.min.y,i&4?bounds.max.z:bounds.min.z);
    for(let i=0;i<27;i++)this.moduleHull[i].set(T.MathUtils.lerp(bounds.min.x,bounds.max.x,(i%3)/2),T.MathUtils.lerp(bounds.min.y,bounds.max.y,((i/3|0)%3)/2),T.MathUtils.lerp(bounds.min.z,bounds.max.z,(i/9|0)/2));
    const hull=new T.MeshStandardMaterial({vertexColors:true,metalness:.58,roughness:.37});
    const crystal=new T.MeshStandardMaterial({vertexColors:true,metalness:.20,roughness:.20,emissive:0x2664f5,emissiveIntensity:.30});
    this.core=new T.MeshStandardMaterial({vertexColors:true,color:0xcdeeff,emissive:0x559fff,emissiveIntensity:2.8,roughness:.2,metalness:.12});
    this.core.color.set(0xffffff);
    tintCrystal(crystal,this.auraColor);tintCrystal(this.core,this.auraColor);
    this.energyMaterials.push(crystal,this.core);
    this.glow=glowMaterial(this.auraDisplay);this.plume=plumeMaterial(this.auraDisplay);
    this.materials.push(hull,crystal,this.core,this.glow,this.plume);
    const plumeGeo=plumeGeometry(),haloGeo=new T.PlaneGeometry(.49,.49);this.ownedGeometry.push(plumeGeo,haloGeo);
    for(const [name,g,m]of [
      ['hull',asset.module_hull,hull],['crystal',asset.module_crystal,crystal],['core',asset.module_core,this.core],
      ['aura',haloGeo,this.glow],['thrust',plumeGeo,this.plume],
    ]as const){
      const geometry=g.clone();this.ownedGeometry.push(geometry);
      const mesh=new T.InstancedMesh(geometry,m,CRYSTAL_MODULE_COUNT);mesh.name='crystal-wings-'+name;
      mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);mesh.frustumCulled=false;mesh.castShadow=name==='hull';mesh.receiveShadow=name==='hull';
      this.group.add(mesh);this.instances.push(mesh);
    }
    this.dock=new T.Group();this.dock.name='crystal-wings-docking-spine';this.group.add(this.dock);
    for(const [name,source,baseMaterial]of [['vertebrae',asset.dock_hull,hull],['spinal-core',asset.dock_core,crystal]]as const){
      const geometry=source.clone(),material=baseMaterial.clone();
      material.onBeforeCompile=baseMaterial.onBeforeCompile;fitSpineMaterial(material);this.materials.push(material);this.ownedGeometry.push(geometry);
      if(name==='spinal-core'){material.emissiveIntensity=.48;this.energyMaterials.push(material);}
      const patches=['spineLeft','spineRight','spineUp','spineDown'].map(name=>{
        const attribute=new T.InstancedBufferAttribute(new Float32Array(CRYSTAL_SPINE_Y.length*3),3);attribute.setUsage(T.DynamicDrawUsage);geometry.setAttribute(name,attribute);return attribute;
      });this.spinePatches.push(patches);
      const mesh=new T.InstancedMesh(geometry,material,CRYSTAL_SPINE_Y.length);mesh.name='crystal-wings-'+name;
      mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);mesh.frustumCulled=false;mesh.castShadow=name==='vertebrae';
      if(mesh.castShadow){mesh.customDepthMaterial=fitSpineMaterial(new T.MeshDepthMaterial({depthPacking:T.RGBADepthPacking}));this.materials.push(mesh.customDepthMaterial);}
      this.dock.add(mesh);this.spineInstances.push(mesh);
    }
    this.airflow=new CrystalWingAirflow(this.group,this.auraDisplay);
    this.loaded=true;this.setAuraColor(this.auraColor.getHex());
  }
  update(dt:number,state:Readonly<CrystalWingState>,position:T.Vector3,rotation:T.Quaternion,scale=1,frame:T.Object3D|null=null,dockAnchor?:CrystalWingDockAnchor,limbAnchors?:CrystalLimbAnchors,bodyPose?:CrystalWingBodyPose,flightFrame?:CrystalWingFlightFrame):void {
    if(this.disposed||this.debris)return;
    this.weaponHover=!!this.weaponFrame&&state.jet==='hover';
    this.uprightToTorso.copy(rotation).invert();
    this.motion.update(dt,state,bodyPose,this.uprightToTorso,!!this.weaponFrame);
    const capeAllowed=!state.grounded&&!state.compact&&!state.attacking&&!state.guarding&&state.aiming<.15&&this.motion.landing<.01&&state.jet!=='none';
    const ascent=capeAllowed&&Number.isFinite(state.verticalAscent)?T.MathUtils.clamp(state.verticalAscent!,0,1):0;
    const descent=capeAllowed?this.motion.verticalDive:0;
    const capeTarget=Math.max(ascent,descent),step=T.MathUtils.clamp(Number.isFinite(dt)?dt:0,0,.1);
    // A critically damped spring, not a follower: the cape gathers and
    // releases over ~0.6 s with no jerk at either end, and a climb hovering at
    // the vertical threshold no longer pumps the whole fan in and out.
    // The exhaust re-aims at once (flame, not a solid); only the blades ease.
    this.capeWake=!this.capeSeeded||capeTarget>=this.capeWake?capeTarget:T.MathUtils.lerp(this.capeWake,capeTarget,1-Math.exp(-4*step));
    if(this.capeWake<1e-5)this.capeWake=0;
    if(!this.capeSeeded){this.verticalCape=capeTarget;this.capeRate=0;}
    else if(step>0){
      const omega=7,h=step/Math.max(1,Math.ceil(step*240)),n=Math.max(1,Math.ceil(step*240));
      for(let k=0;k<n;k++){this.capeRate+=(omega*omega*(capeTarget-this.verticalCape)-2*omega*this.capeRate)*h;this.verticalCape+=this.capeRate*h;}
      this.verticalCape=T.MathUtils.clamp(this.verticalCape,0,1);
    }
    if(this.verticalCape<1e-5&&capeTarget===0){this.verticalCape=0;this.capeRate=0;}
    this.capeSeeded=true;
    this.capeSpread=T.MathUtils.lerp(1,.84,T.MathUtils.smoothstep(Math.hypot(Number.isFinite(state.speed)?state.speed:0,Number.isFinite(state.climb)?state.climb:0),12,48));
    this.capeBackOffset=Math.max(0,(Number.isFinite(state.backOffset)?state.backOffset:.21)-.21);
    this.capeMotion.update(dt,state,capeAllowed?this.verticalCape:0);
    // The cape stays behind the torso through inversion. Its nozzle axis turns
    // from radial down to radial up as the body enters the dive.
    if(ascent+descent>1e-8)this.capeAxis.set(0,Math.cos(Math.PI*descent/(ascent+descent)),Math.sin(Math.PI*descent/(ascent+descent)));
    this.capeExhaust.copy(this.capeAxis).applyQuaternion(this.capeMotion.rotations[1]).negate().applyQuaternion(this.uprightToTorso);
    this.diveExhaust.set(0,1,0).applyQuaternion(this.uprightToTorso);
    this.group.visible=this.loaded&&state.visible;
    this.group.position.copy(position);this.group.quaternion.copy(rotation);this.group.scale.setScalar(scale);
    this.time+=T.MathUtils.clamp(Number.isFinite(dt)?dt:0,0,.1);
    if(!this.loaded||!state.visible){this.dynamics.reset();this.inertia.reset();this.limbDynamics.reset();this.runTrailing.set(0,0,0);this.airflow?.reset();return;}
    if(this.frame!==frame){this.frame=frame;this.dynamics.reset();this.inertia.reset();this.limbDynamics.reset();this.runTrailing.set(0,0,0);this.airflow?.reset();}
    frame?.updateWorldMatrix(true,false);this.group.updateWorldMatrix(true,false);
    if(frame)this.worldToFrame.copy(frame.matrixWorld).invert();else this.worldToFrame.identity();
    this.worldToGroup.copy(this.group.matrixWorld).invert();this.groupToFrame.copy(this.worldToFrame);
    this.groupToFrame.multiply(this.group.matrixWorld);
    this.frameToGroup.copy(this.groupToFrame).invert();
    this.frameRotation.setFromRotationMatrix(this.matrix.extractRotation(this.groupToFrame));
    this.inverseRotation.copy(this.frameRotation).invert();
    // Aiming only: at rest the reticle sits on the back itself, which the
    // player expects to see. The weight follows the camera's own ADS ease.
    let sightWeight=this.sight.active&&!state.compact&&this.motion.landing<.01
      ?T.MathUtils.smoothstep(Number.isFinite(state.aiming)?state.aiming:0,0,.6):0;
    if(sightWeight>0){
      this.sightInverse.copy(this.group.matrix).invert();
      this.sightOrigin.copy(this.sight.origin).applyMatrix4(this.sightInverse);
      this.sightDir.copy(this.sight.dir).transformDirection(this.sightInverse);
      this.sightUp.copy(this.sight.up).transformDirection(this.sightInverse);
      this.sightUp.addScaledVector(this.sightDir,-this.sightUp.dot(this.sightDir)).normalize();
      // While the camera is still swinging over the shoulder the reticle is on
      // the torso itself; pushing blades away from it there only flings them.
      // Engage as the sight line clears the spine (this group's local Y axis).
      this.sightPoint.set(this.sightDir.z,0,-this.sightDir.x);
      const cross=this.sightPoint.length();
      const clearance=cross>.2?Math.abs(this.sightOrigin.dot(this.sightPoint))/cross:Math.hypot(this.sightOrigin.x,this.sightOrigin.z);
      sightWeight*=T.MathUtils.smoothstep(clearance,.28,.46);
    }
    const hold=flightFrame&&!state.compact&&state.jet!=='dive'&&this.motion.landing<.01?flightFrame.weight:0;
    if(hold>0){
      this.flightCompensation.copy(this.uprightToTorso).multiply(flightFrame!.rotation);
      this.flightOffset.copy(flightFrame!.offset).applyQuaternion(this.uprightToTorso).divideScalar(scale);
    }
    for(let i=0;i<CRYSTAL_BACK_COUNT;i++){
      this.local.copy(this.motion.positions[i]);this.localRotation.copy(this.motion.rotations[i]);
      // Shorter cape blades fit the close-back rows without changing other formations.
      this.backScales[i].copy(this.motion.scales[i]).multiplyScalar(T.MathUtils.lerp(1,.86,T.MathUtils.smootherstep(this.verticalCape,0,1)));
      if(hold>0){
        this.flightTarget.copy(this.local).applyQuaternion(this.flightCompensation).add(this.flightOffset);
        this.flightRotation.copy(this.flightCompensation).multiply(this.localRotation);
        this.local.lerp(this.flightTarget,hold);this.localRotation.slerp(this.flightRotation,hold);
      }
      if(this.verticalCape>0)this.applyVerticalCape(i,this.local,this.localRotation);
      if(sightWeight>0)this.clearSight(this.local,this.localRotation,this.backScales[i],sightWeight);
      this.localTargets[i].copy(this.local);this.localTargetRotations[i].copy(this.localRotation);
      this.targets[i].copy(this.local).applyMatrix4(this.groupToFrame);
      this.targetRotations[i].copy(this.frameRotation).multiply(this.localRotation);
    }
    const spread=T.MathUtils.clamp((Math.abs(this.motion.positions[0].x)-.14)/.5,0,1);
    const running=state.grounded&&state.jet==='none'&&!state.compact&&!state.attacking&&!state.guarding&&state.aiming<.15&&this.motion.landing<.01;
    const trail=running?.18*T.MathUtils.smoothstep(Math.abs(state.speed),2.8,7)*scale:0;
    // Rearward in the player's upright frame, then carried into the spring's
    // stable planet frame. Easing the allowance also softens starts and stops.
    this.runTrailTarget.set(0,0,trail).applyQuaternion(this.uprightToTorso).applyQuaternion(this.frameRotation);
    if(dt>.2||state.compact)this.runTrailing.set(0,0,0);
    else this.runTrailing.lerp(this.runTrailTarget,1-Math.exp(-8*Math.max(0,Math.min(Number.isFinite(dt)?dt:0,.1))));
    // The tethered follower lags the formation inside its authored lanes.
    this.dynamics.update(dt,this.targets,this.targetRotations,scale,spread,this.runTrailing);
    if(this.naturalMotion){
      // Then the body and the air swing each side as one wing on top of it.
      for(let i=0;i<CRYSTAL_BACK_COUNT;i++){
        this.localTargets[i].copy(this.dynamics.positions[i]).applyMatrix4(this.frameToGroup);
        this.localTargetRotations[i].copy(this.inverseRotation).multiply(this.dynamics.rotations[i]);
      }
      this.anchorPosition.setFromMatrixPosition(this.groupToFrame);
      const brace=Math.max(T.MathUtils.clamp(Number.isFinite(state.aiming)?state.aiming:0,0,1),state.compact?1:0,
        state.attacking||state.guarding?.6:0,this.motion.landing*.4,
        // The vertical cape packs the blades close; it flies as one braced surface.
        T.MathUtils.smoothstep(this.verticalCape,0,1)*.85);
      this.inertia.update(dt,{targets:this.localTargets,rotations:this.localTargetRotations,anchorPosition:this.anchorPosition,
        anchorRotation:this.frameRotation,scale,backOffset:state.backOffset,brace,lag:false});
    }
    for(let i=0;i<CRYSTAL_BACK_COUNT;i++){
      if(this.naturalMotion){this.local.copy(this.inertia.positions[i]);this.localRotation.copy(this.inertia.rotations[i]);}
      else{
        this.local.copy(this.dynamics.positions[i]).applyMatrix4(this.frameToGroup);
        this.localRotation.copy(this.inverseRotation).multiply(this.dynamics.rotations[i]);
      }
      // Inward travel is smaller than outward slack, protecting the clothing
      // when the player abruptly reverses. The plate itself has no delayed pose.
      this.flightTarget.copy(this.targets[i]).applyMatrix4(this.frameToGroup);
      // All cape sockets stay behind the animated torso. The following spring
      // can trail away from the back, but cannot pull a blade into the outfit.
      // Outboard blades may swing forward beside the torso; inboard ones may not.
      this.local.z=Math.max(this.local.z,this.flightTarget.z-(this.naturalMotion&&Math.abs(this.flightTarget.x)>.5?.07:.018));
      // Clearance is in the avatar's radial-up frame, not global world Y.
      // Tucking while rolling cannot send a crystal point below the feet plane.
      if(state.grounded&&(state.compact||this.motion.landing>.01)){
        this.local.applyMatrix4(this.group.matrix);
        // .48 m encloses the complete source module about its socket origin.
        const clearance=.48*scale*this.backScales[i].y+.025;
        if(this.local.y<clearance){this.local.y=clearance;this.local.applyMatrix4(this.matrix.copy(this.group.matrix).invert());}
        else this.local.applyMatrix4(this.matrix.copy(this.group.matrix).invert());
      }
      this.matrix.compose(this.local,this.localRotation,this.backScales[i]);
      for(const mesh of this.instances)mesh.setMatrixAt(i,this.matrix);
      this.writePlume(i,this.local,this.localRotation,this.backScales[i]);
      if(i%3<2)this.airflowSources[(i<3?0:2)+i%3].copy(this.plumePosition);
      const bright=1+this.motion.pulses[i]*2+.35*Math.max(this.motion.launchPulse,this.motion.divePulse);
      this.color.setRGB(bright,bright,bright);this.instances[2].setColorAt(i,this.color);
    }
    if(limbAnchors){
      this.group.getWorldQuaternion(this.worldTorso);
      this.capeWorldExhaust.copy(this.capeExhaust).applyQuaternion(this.worldTorso);
      this.limbMotion.update(dt,state.jet!=='none'||this.motion.landing>.3,state.compact||state.wallSide!==0,this.worldTorso,scale,limbAnchors,state.jumpThrust,state.approach,this.motion.boostSpread,this.capeWorldExhaust,this.verticalCape);
      if(frame)frame.getWorldQuaternion(this.worldFrameInverse).invert();else this.worldFrameInverse.identity();
      for(let i=0;i<CRYSTAL_LIMB_COUNT;i++){
        this.limbTargets[i].copy(this.limbMotion.positions[i]).applyMatrix4(this.worldToFrame);
        this.limbRotations[i].copy(this.worldFrameInverse).multiply(this.limbMotion.rotations[i]);
      }
      this.limbDynamics.update(dt,this.limbTargets,this.limbRotations,scale,this.limbMotion.deployment*.45);
      for(let i=0;i<CRYSTAL_LIMB_COUNT;i++){
        this.local.copy(this.limbDynamics.positions[i]).applyMatrix4(this.frameToGroup);
        this.localRotation.copy(this.inverseRotation).multiply(this.limbDynamics.rotations[i]);
        // A spring may trail outward, but never pull the pod through its limb.
        this.plumePosition.copy(this.limbMotion.positions[i]).applyMatrix4(this.worldToGroup);
        this.limbOut.copy(this.limbMotion.outward[i]).transformDirection(this.worldToGroup);
        const inward=this.limbDelta.copy(this.local).sub(this.plumePosition).dot(this.limbOut);
        if(inward<-.008)this.local.addScaledVector(this.limbOut,-.008-inward);
        // The origin can be outside while angular lag swings a tip into the
        // arm. Keep the rotated solid bounds outside the live limb plane too.
        // Eight cached corners avoid raycasting or deforming any source mesh.
        const limb=limbAnchors.limbs[i];
        // Calf pods also pass the boot's asymmetric outer rim, which projects
        // beyond the centreline fit. Keep that rim inside the protected plane.
        this.limbSurface.copy(limb.position).addScaledVector(limb.outward,limb.radius+(i<2?.004:.025)*scale).applyMatrix4(this.worldToGroup);
        let clearance=Infinity;
        for(const corner of this.moduleCorners){
          this.limbDelta.copy(corner).multiply(this.limbMotion.scales[i]).applyQuaternion(this.localRotation).add(this.local).sub(this.limbSurface);
          clearance=Math.min(clearance,this.limbDelta.dot(this.limbOut));
        }
        if(clearance<0)this.local.addScaledVector(this.limbOut,-clearance);
        if(state.grounded&&(state.compact||this.motion.landing>.01)){
          this.local.applyMatrix4(this.group.matrix);
          this.local.y=Math.max(this.local.y,.48*scale*this.limbMotion.scales[i].y+.025);
          this.local.applyMatrix4(this.matrix.copy(this.group.matrix).invert());
        }
        this.matrix.compose(this.local,this.localRotation,this.limbMotion.scales[i]);
        for(const mesh of this.instances)mesh.setMatrixAt(CRYSTAL_BACK_COUNT+i,this.matrix);
        this.writePlume(CRYSTAL_BACK_COUNT+i,this.local,this.localRotation,this.limbMotion.scales[i]);
        this.color.setRGB(1,1,1);this.instances[2].setColorAt(CRYSTAL_BACK_COUNT+i,this.color);
      }
    }else{
      this.matrix.makeScale(0,0,0);
      for(let i=CRYSTAL_BACK_COUNT;i<CRYSTAL_MODULE_COUNT;i++)for(const mesh of this.instances)mesh.setMatrixAt(i,this.matrix);
    }
    if(this.weaponFrame)this.weaponMorph.update(dt,this.weaponFrame,this.instances,this.group);
    for(const mesh of this.instances)mesh.instanceMatrix.needsUpdate=true;
    if(this.instances[2].instanceColor)this.instances[2].instanceColor.needsUpdate=true;
    if(this.dock){
      this.group.getWorldQuaternion(this.inverseRotation).invert();
      for(let i=0;i<CRYSTAL_SPINE_Y.length;i++){
        const segment=dockAnchor?.segments[i];
        if(segment?.attached){
          this.local.copy(segment.position);this.group.worldToLocal(this.local);
          this.localRotation.copy(this.inverseRotation).multiply(segment.rotation);
          this.spineScale.set(CRYSTAL_SPINE_WIDTH[i],CRYSTAL_SPINE_HEIGHT[i],1);
          for(const patches of this.spinePatches)for(let edge=0;edge<4;edge++){
            const p=segment.patch[edge];patches[edge].setXYZ(i,p.x,p.y,p.z);
          }
        }else{
          // There is no hovering fallback plate while the body is streaming.
          this.local.set(0,0,0);this.localRotation.identity();this.spineScale.setScalar(0);
        }
        this.matrix.compose(this.local,this.localRotation,this.spineScale);
        for(const mesh of this.spineInstances)mesh.setMatrixAt(i,this.matrix);
      }
      for(const mesh of this.spineInstances)mesh.instanceMatrix.needsUpdate=true;
      for(const patches of this.spinePatches)for(const attribute of patches)attribute.needsUpdate=true;
    }
    const startup=Math.max(this.motion.launchPulse,this.motion.divePulse),emission=this.motion.thrust+.24*this.motion.divePulse+(this.weaponHover?.14*this.weaponMorph.amount:0);
    this.glow!.uniforms.strength.value=.22+.18*emission;
    this.plume!.uniforms.strength.value=emission;this.plume!.uniforms.time.value=this.time;
    this.plume!.uniforms.trailLength.value=.20+1.10*T.MathUtils.smoothstep(emission,.20,.90)+.35*T.MathUtils.smoothstep(Math.abs(state.speed),22,40)+.50*startup;
    this.core!.emissiveIntensity=2.1+.5*emission+.12*Math.sin(this.time*2)+.65*startup+2.5*this.weaponMorph.flash;
    this.airflow!.update(dt,this.airflowSources,this.groupToFrame,this.frameToGroup,Math.hypot(state.speed,state.climb),
      !state.grounded&&!state.compact&&!state.attacking&&!state.guarding&&state.aiming<.15&&this.motion.landing<.01&&(state.jet==='glide'||state.jet==='dive'));
  }
  /** Move one module rigidly out of the reticle cone. The push radiates from a
   * point above the sight line, so blades beside or below it always travel
   * outward/down: a module crossing under the reticle never flips sides. */
  private clearSight(position:T.Vector3,rotation:T.Quaternion,size:T.Vector3,weight:number):void{
    const tan=this.sight.tan;
    this.sightPoint.copy(position).sub(this.sightOrigin);
    const depth=this.sightPoint.dot(this.sightDir);
    if(depth<=.05)return;
    this.sightPush.copy(this.sightPoint).addScaledVector(this.sightDir,-depth).addScaledVector(this.sightUp,-depth*tan*1.5);
    if(this.sightPush.lengthSq()<1e-10)this.sightPush.copy(this.sightUp).negate();
    this.sightPush.normalize();
    let shift=0;
    for(const corner of this.moduleHull){
      this.sightPoint.copy(corner).multiply(size).applyQuaternion(rotation).add(position).sub(this.sightOrigin);
      const t=this.sightPoint.dot(this.sightDir);
      if(t<=.05)continue;
      const r=t*tan;
      this.sightRadial.copy(this.sightPoint).addScaledVector(this.sightDir,-t);
      const rho2=this.sightRadial.lengthSq();
      if(rho2>=r*r)continue;
      const along=this.sightRadial.dot(this.sightPush);
      shift=Math.max(shift,-along+Math.sqrt(along*along-rho2+r*r));
    }
    position.addScaledVector(this.sightPush,shift*weight);
  }
  private writePlume(index:number,position:T.Vector3,rotation:T.Quaternion,size:T.Vector3):void{
    this.plumePosition.set(0,-.435,.012).multiply(size).applyQuaternion(rotation).add(position);
    // Keep the main wake rearward, with a small steering correction per pair.
    // Limb pods use the same correction at half strength, like trim thrusters.
    const back=index<CRYSTAL_BACK_COUNT;
    const steeringIndex=back?index:(position.x<0?2:5);
    this.plumeRotation.setFromUnitVectors(DOWN,this.motion.exhaust[steeringIndex]);
    if(!back)this.plumeRotation.slerp(IDENTITY,.5);
    if(this.capeWake>0){this.capeRotation.setFromUnitVectors(DOWN,this.capeExhaust);this.plumeRotation.slerp(this.capeRotation,this.capeWake);}
    // During the mirrored tuck/start gesture the torso is still rotating.
    // Its wake already points opposite the intended downward launch, then
    // hands off to the identical upward wake of the established dive cape.
    if(this.motion.diveWake>0){this.capeRotation.setFromUnitVectors(DOWN,this.diveExhaust);this.plumeRotation.slerp(this.capeRotation,this.motion.diveWake);}
    // The free upper pair carries hover even when ADS bends the torso down.
    // Its exhaust opposes gravity in the player's frame, not the chest frame.
    if(this.weaponHover&&(index===0||index===3))this.plumeRotation.copy(this.uprightToTorso);
    const gain=this.motion.thrustGains[steeringIndex];
    this.plumeScale.set(size.x,(back?.82:1.05)*gain,size.z);
    this.matrix.compose(this.plumePosition,this.plumeRotation,this.plumeScale);this.instances[4].setMatrixAt(index,this.matrix);
  }
  /** Fold the ordinary fan into a cupped cape without orbiting around the
   * chest. Pair-specific aft lanes keep rigid blades separate during the fold. */
  private applyVerticalCape(index:number,position:T.Vector3,rotation:T.Quaternion):void{
    const k=this.verticalCape,side=index<3?-1:1,rank=index%3,row=VERTICAL_CAPE[rank];
    const bend=this.capeMotion.bend[rank],inside=side*bend;
    this.capePoint.set(side*row[0]*this.capeSpread*(1-inside*.16),
      row[1]+this.capeMotion.axial[rank]-inside*.055,
      row[2]+this.capeBackOffset+inside*.11)
      .applyQuaternion(this.capeMotion.rotations[rank]);
    position.lerp(this.capePoint,T.MathUtils.smootherstep(k,0,1));
    // Separate lanes through the fold, kept tight so the fan sweeps back as
    // one wing rather than dispersing: the lower rows pass a little further aft.
    const fold=Math.sin(Math.PI*k);
    position.z+=(.06+rank*.09)*fold;
    position.y+=(1-rank)*.07*fold;
    // The landing recovery swings the forearms beside the leading row. Let
    // that pair return behind the limb pods before it seats on the back.
    if(rank===0)position.z+=.32*this.motion.landing*fold;
    // Crystal bodies lie along the posed back instead of counter-rotating to
    // world vertical. Their separately aimed exhaust still follows travel.
    this.capeModuleAxis.set(0,1,0).applyQuaternion(this.capeMotion.rotations[rank]);
    this.capeRight.set(1,0,-side*.16).addScaledVector(this.capeModuleAxis,-this.capeRight.dot(this.capeModuleAxis)).normalize();
    this.capeNormal.crossVectors(this.capeRight,this.capeModuleAxis).normalize();
    this.capeRotation.setFromRotationMatrix(this.capeBasis.makeBasis(this.capeRight,this.capeModuleAxis,this.capeNormal));
    // The long facets follow the diagonals of the V. The exhaust still follows
    // the measured travel axis, so banking does not redirect the player's boost.
    this.capeFeather.setFromAxisAngle(BACK,-side*(.22+inside*.045));
    this.capeRotation.multiply(this.capeFeather);
    rotation.slerp(this.capeRotation,T.MathUtils.smootherstep(k,.10,.90));
    // A steeply pitched torso can aim the long blade through the back even
    // with a rear socket. Keep its complete rotated bounds behind the torso.
    let rearExtent=Infinity;
    for(const corner of this.moduleCorners){
      this.limbDelta.copy(corner).multiply(this.backScales[index]).applyQuaternion(rotation);
      rearExtent=Math.min(rearExtent,this.limbDelta.z);
    }
    position.z=Math.max(position.z,(.18+this.capeBackOffset)*T.MathUtils.smootherstep(k,.45,.95)-rearExtent);
  }
  resetForTeleport():void{
    this.weaponMorph.reset();
    this.endDeath();
    this.verticalCape=0;this.capeRate=0;this.capeWake=0;this.capeSeeded=false;this.capeAxis.set(0,1,0);this.motion.clearVerticalHistory();this.capeMotion.reset();
    this.dynamics.reset();this.inertia.reset();this.limbDynamics.reset();
  }
  get deathDebrisActive():boolean{return this.debris!==null;}
  /** Velocity/contact are in the parent avatar frame. Capture every original
   * instance before detaching; changing skeleton poses cannot move the debris. */
  startDeath(velocity:T.Vector3,impact:T.Vector3,contact:DeathContactResolver,seed:number):void{
    if(!this.loaded||this.disposed||this.debris)return;
    this.group.updateMatrix();this.deathToGroup.copy(this.group.matrix).invert();
    this.deathMatrices=this.instances.map(mesh=>Array.from({length:CRYSTAL_MODULE_COUNT},(_,i)=>{const matrix=new T.Matrix4();mesh.getMatrixAt(i,matrix);return matrix;}));
    const matrices=this.deathMatrices[0].map(matrix=>matrix.clone().premultiply(this.group.matrix));
    this.debris=new CrystalWingDebris(matrices,new T.Box3().setFromPoints(this.moduleCorners),velocity,impact,seed);
    this.deathContact=contact;this.airflow?.reset();if(this.dock)this.dock.visible=false;
    // Powered trails and screen-facing halos shut off; all ten solid modules
    // keep their original shape, colour and captured position.
    this.instances[3].visible=false;this.instances[4].visible=false;
  }
  stepDeath(dt:number):void{
    if(!this.debris||!this.deathContact)return;
    this.debris.step(dt,this.deathContact);
    for(let i=0;i<CRYSTAL_MODULE_COUNT;i++){
      this.debris.matrix(i,this.matrix).premultiply(this.deathToGroup);
      for(let part=0;part<3;part++)this.instances[part].setMatrixAt(i,this.matrix);
    }
    for(let part=0;part<3;part++)this.instances[part].instanceMatrix.needsUpdate=true;
  }
  endDeath():void{
    if(!this.debris)return;
    this.debris=null;this.deathContact=null;
    for(let part=0;part<this.instances.length;part++){
      const mesh=this.instances[part];
      for(let i=0;i<CRYSTAL_MODULE_COUNT;i++)mesh.setMatrixAt(i,this.deathMatrices[part][i]);
      mesh.instanceMatrix.needsUpdate=true;mesh.visible=true;
    }
    this.deathMatrices=[];if(this.dock)this.dock.visible=true;
    this.dynamics.reset();this.inertia.reset();this.limbDynamics.reset();this.motion.clearVerticalHistory();
  }
  shoot():void{this.motion.shoot();this.weaponMorph.shoot();}
  dispose():void{
    if(this.disposed)return;this.disposed=true;this.group.removeFromParent();
    this.airflow?.dispose();
    for(const mesh of this.instances)mesh.dispose();
    for(const mesh of this.spineInstances)mesh.dispose();
    for(const m of this.materials)m.dispose();for(const g of this.ownedGeometry)g.dispose();
    this.group.clear();this.loaded=false;
  }
}
